// frontend/src/types/loiOverride.ts
/**
 * The frontend's copy of the LOI override types (backend: services/loiOverrideTypes.ts, loiOverrides.ts,
 * loiHouseStyle.ts, dealScenarios.ts). ⛔ A frontend file never imports backend source; this copy is pinned to
 * the backend's lists by backend/src/services/loiOverride.contract.test.ts - change both together.
 */
export const LOI_SECTION_KEYS = ['purchase_price', 'buyer_profile', 'exchange_1031', 'earnest_money', 'due_diligence', 'seller_deliverables', 'close_of_escrow', 'assignment', 'escrow_title', 'psa', 'closing_costs', 'confidentiality', 'brokerage', 'non_binding'] as const;
export type LoiSectionKey = (typeof LOI_SECTION_KEYS)[number] | `custom_${number}`;

/**
 * D3: each standard section's title as his letters print it (both eras print the same fourteen), less its colon - how a
 * section is named when the letter on screen does not have it. ⛔ A key is never shown to him. Pinned to the builder's
 * own titles by backend/src/services/loiOverride.contract.test.ts.
 */
export const LOI_SECTION_TITLES: Record<(typeof LOI_SECTION_KEYS)[number], string> = {
  purchase_price: 'Purchase Price',
  buyer_profile: 'Buyer Profile',
  exchange_1031: '1031 Exchange / Closing Coordination',
  earnest_money: 'Earnest Money Deposit',
  due_diligence: 'Contingency / Due Diligence Period',
  seller_deliverables: 'Seller Deliverables',
  close_of_escrow: 'Close of Escrow & Possession',
  assignment: 'Assignment',
  escrow_title: 'Escrow & Title',
  psa: 'Purchase & Sale Agreement',
  closing_costs: 'Closing Costs',
  confidentiality: 'Confidentiality',
  brokerage: 'Brokerage',
  non_binding: 'Non-Binding',
};

export const OVERRIDE_ACTIONS = ['rewrite', 'verbatim', 'remove', 'add_after'] as const;
export type OverrideAction = (typeof OVERRIDE_ACTIONS)[number];
export const OVERRIDE_SOURCES = ['chat', 'document', 'house_style'] as const;
export type OverrideSource = (typeof OVERRIDE_SOURCES)[number];

export interface SectionOverride {
  id: string;
  section: LoiSectionKey;
  action: OverrideAction;
  newKey?: `custom_${number}`;
  title?: string;
  paragraphs?: string[];
  asked: string;
  source: OverrideSource;
  flags: string[];
  createdBy: string;
  createdAt: string;
}

export interface PreviewSection { key: string; title: string; paragraphs: string[]; changedBy: string[]; houseStyle: string[] }
/**
 * ⭐ His ruling 2026-09-30: an applied change a LATER change to the same section took out of the letter (latest wins).
 * `whole`: nothing of it is left. `words`: its words went, but its title or the section it added stays. `section`: a
 * later change removed its section. `by`: the later change's id. Pinned to the engine's by the contract test.
 */
export interface Replaced { by: string; what: 'whole' | 'words' | 'section' }
/** `replaced` is present only on a change a later one replaced. */
export interface OverrideOutcome { id: string; applied: boolean; reason: string | null; replaced?: Replaced }
export interface RemovedSection { key: string; title: string; overrideId: string }
export interface DanglingNotice { inSection: string; inTitle: string; refersTo: string; refersToTitle: string }
export interface HouseStyleSummary { id: string; section: string; action: OverrideAction; title: string | null; asked: string; skipped: boolean }
export interface LoiPreview {
  sections: PreviewSection[];
  outcomes: OverrideOutcome[];
  removed: RemovedSection[];
  dangling: DanglingNotice[];
  houseStyle: HouseStyleSummary[];
}
export interface DraftedChange {
  section: LoiSectionKey;
  action: 'rewrite' | 'remove' | 'add_after';
  title: string | null;
  paragraphs: string[];
  flags: string[];
}
export interface LoiDraft { changes: DraftedChange[]; refused: string[] }
export interface HouseStyleRule {
  id: string;
  era: 'september_2026' | 'june_2026';
  section: LoiSectionKey;
  action: OverrideAction;
  title: string | null;
  paragraphs: string[];
  newKey: `custom_${number}` | null;
  asked: string;
  createdAt: string;
}
export interface DealScenario {
  id: string;
  dealId: string;
  label: string;
  purchasePrice: number;
  basis: {
    kind: 'price_at_cap';
    targetCapPct: number;
    goingInNoi: number;
    analysisPrice: number;
    analysisCapPct: number;
    analysisId: string;
    /** `deal_analyses.updated_at` - NULLABLE (Ruling 27a), so a what-if can arrive with no date to name. */
    analysisSavedAt: string | null;
  };
  createdAt: string;
  promotedAt: string | null;
}
